import React from "react";
import Icon from "@components/Icon";
import {
    PREREQUISITES_BY_PROVIDER,
    type PrerequisitesListItem,
    type PrerequisitesProvider,
} from "./prerequisitesRegistry";

export type { PrerequisitesListItem, PrerequisitesProvider };

interface PrerequisitesListProps {
    provider?: PrerequisitesProvider;
    items?: PrerequisitesListItem[];
    title?: string;
}

const CodeTag: React.FC<{ label: string }> = ({ label }) => (
    <span className="inline-flex shrink-0 items-center justify-center rounded bg-gray-10 px-0.5 text-[10px] font-normal leading-[22px] text-text-body">
        {label}
    </span>
);

const PrerequisitesList: React.FC<PrerequisitesListProps> = ({
    provider,
    items,
    title = "Prerequisites",
}) => {
    const resolvedItems = items ?? (provider ? PREREQUISITES_BY_PROVIDER[provider] : []);

    if (!resolvedItems.length) {
        return null;
    }

    return (
        <div className="flex flex-col gap-3">
            <p className="px-4 text-[14px] font-semibold leading-[22px] text-text-body">
                {title}
            </p>
            <div className="mx-4 overflow-hidden rounded border border-gray-20 bg-white px-4">
                {resolvedItems.map((item, index) => (
                    <div
                        key={item.title}
                        className={`flex gap-2 py-2 ${
                            index < resolvedItems.length - 1 ? "border-b border-gray-20" : ""
                        }`}
                    >
                        <Icon
                            name="tick_v2"
                            width={16}
                            height={16}
                            className="mt-0.5 shrink-0 fill-success-100"
                        />
                        <div className="flex min-w-0 flex-1 flex-col">
                            <p className="text-[12px] leading-[22px] text-text-body">
                                <span className="font-semibold">{item.title}</span>
                                <span className="font-normal"> {item.description}</span>
                            </p>
                            {item.codeTagsLine ? (
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-[12px] font-normal leading-[22px] text-text-body">
                                        {item.codeTagsLine.prefix}
                                    </span>
                                    {item.codeTagsLine.tags.map((tag, tagIndex) => (
                                        <React.Fragment key={tag}>
                                            {tagIndex > 0 ? (
                                                <span className="text-[12px] font-normal leading-[22px] text-text-body">
                                                    /
                                                </span>
                                            ) : null}
                                            <CodeTag label={tag} />
                                        </React.Fragment>
                                    ))}
                                    <span className="text-[12px] font-normal leading-[22px] text-text-body">
                                        {item.codeTagsLine.suffix}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(PrerequisitesList);
